/**
 * Actions
 *
 * Keep in mind that actions are complex, and this isn't the only component that renders them. Make sure you check how
 * changes to methods that return TemplateResults will affect actions inside loops and filters.
 */

// Import dependencies
import './resend-button';
import './run-now-button';
import './cancel-button';

import { AutomatorLogItem } from '@automator/features/page-logs/components/log-item/_base';
import { unsafeCSS, css, html } from 'lit';
import { property, customElement } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';

// Import global utils
import { isEmpty } from '@automator/shared/utils/is-empty';

// Import escapeHTML
import { escapeHTML } from '@wordpress/escape-html';

// Import i18n functions
import { __, sprintf } from '@wordpress/i18n';

// Import html-entities
import { decode } from 'html-entities';

// Import style for web component
import styles from './web-component-style.scss?inline';

// eslint-disable-next-line require-jsdoc
@customElement( 'uap-log-item-action' )
export class AutomatorLogAction extends AutomatorLogItem {
	// Define style
	static styles = css`${ unsafeCSS( styles ) }`;

	/**
	 * The message returned by the result of an action.
	 *
	 * @type {String}
	 */
	@property( { type: String, attribute: false } ) accessor resultMessage = '';

	/**
	 * Determines whether the action can be re-run.
	 *
	 * @type {Boolean}
	 */
	@property( { type: Boolean, attribute: 'can-rerun' } ) accessor canReRun = false;

	/**
	 * Determines whether the action can be run immediately.
	 *
	 * @type {Boolean}
	 */
	@property( { type: Boolean, attribute: 'can-run-now' } ) accessor canRowNow = false;

	/**
	 * Determines whether the action can be canceled.
	 *
	 * @type {Boolean}
	 */
	@property( { type: Boolean, attribute: 'can-cancel' } ) accessor canCancel = false;

	/**
	 * The ID of the log item associated with this action.
	 *
	 * @type {String}
	 */
	@property( { type: String, attribute: 'item-log-id' } ) accessor itemLogID;

	/**
	 * Returns the HTML of the section that shows the number of runs
	 * This will return HTML only if there were more than 1 runs.
	 *
	 * @return {TemplateResult} The number of runs
	 */
	get _templateRuns() {
		// Check if there if there is more than one, otherwise don't return anything
		if ( this.runs.length <= 1 ) {
			return html``;
		}

		return html`
			<div class="item__detail">
				<uo-icon class="item__detail-icon" id="hashtag"></uo-icon>

				${ sprintf(
					/* translators: 1. Is a number */
					escapeHTML( __( '%1$s tries', 'uncanny-automator' ) ),
					this.runs.length
				) }
			</div>
		`;
	}

	/**
	 * Creates the object passed to the sidebar
	 *
	 * @return {Object} The sidebar content
	 */
	get sidebarContent() {
		return {
			title: escapeHTML( __( 'Action', 'uncanny-automator' ) ),
			summary: this.sidebarSummary,
			runs: this.sidebarRuns
		}
	}

	/**
	 * Creates the summary for the sidebar
	 *
	 * @return {Object} The sidebar summary
	 */
	get sidebarSummary() {
		// Summary items
		const items = [];

		// Add status
		items.push( {
			label: escapeHTML( __( 'Status', 'uncanny-automator' ) ),
			value: html`
				<uap-log-status status-id="${ this.statusID }"></uap-log-status>
			`
		} );

		// Add start and completion date
		// If it's a range, add "Start date" and "End date". If there is no end date, then just add "Date"
		const dateIsRange = this.startDate !== this.endDate && ! isEmpty( this.endDate );

		// Check if there is a start date (it won't for skipped actions)
		if ( ! isEmpty ( this.startDate ) ) {
			items.push( {
				label: dateIsRange ? escapeHTML( __( 'Start date', 'uncanny-automator' ) ) : escapeHTML( __( 'Date', 'uncanny-automator' ) ),
				value: this.startDate
			} );

			if ( dateIsRange ) {
				// Add "End date"
				items.push( {
					label: escapeHTML( __( 'End date', 'uncanny-automator' ) ),
					value: this.endDate
				} );
			}
		}

		// Add the runs, but only if there is more than one
		if ( this.runs.length > 1 ) {
			items.push( {
				label: escapeHTML( __( 'Runs', 'uncanny-automator' ) ),
				value: this.runs.length
			} );
		}

		// Check if there is an error message
		if ( ! isEmpty( this.resultMessage ) ) {
			items.push( {
				label: escapeHTML( __( 'Notes', 'uncanny-automator' ) ),
				value: decode( this.resultMessage )
			} );
		}

		// Add the tasks that we can perform in the sidebar, such as re-run
		const actionTasks = [];

		// Check if the action can re-run
		if ( this.canReRun ) {
			actionTasks.push(
				html`
					<uap-log-item-action-resend-button
						item-id="${ this.id }"
						item-log-id="${ this.itemLogID }"

						?import-mode=${ this.isImportMode }
					>
					</uap-log-item-action-resend-button>
				`
			);
		}

		// Check if the action can be run on demand
		if ( this.canRowNow ) {
			actionTasks.push(
				html`
					<uap-log-item-action-run-now-button
						item-id="${ this.id }"
						item-log-id="${ this.itemLogID }"

						?import-mode=${ this.isImportMode }
					>
					</uap-log-item-action-run-now-button>
				`
			);
		}

		// Check if the action can cancel (schedule)
		if ( this.canCancel ) {
			actionTasks.push(
				html`
					<uap-log-item-action-cancel-button
						item-id="${ this.id }"
						item-log-id="${ this.itemLogID }"

						?import-mode=${ this.isImportMode }
					>
					</uap-log-item-action-cancel-button>
				`
			);
		}

		if ( ! isEmpty( actionTasks ) ) {
			items.push( {
				label: escapeHTML( __( 'Events', 'uncanny-automator' ) ),
				value: html`
					<div class="item__value-buttons">
						${ repeat(
							actionTasks,
							( task, index ) => index,
							task => task
						) }
					</div>
				`
			} );
		}

		return {
			title: html`
				<uap-log-integration-icon integration-id="${ this.integrationCode }" show-tooltip></uap-log-integration-icon>
				<uap-log-sentence 
					.sentence="${ this.titleHTML }"
					custom-name="${ this.titleCustomName || '' }"
				></uap-log-sentence>
			`,
			items: items
		}
	}

	/**
	 * Creates the array for the sidebar to render the action runs
	 *
	 * @return {Array} The array of runs
	 */
	get sidebarRuns() {
		if ( this.runs.length === 0 ) {
			return [];
		}

		const runs = [];

		// Get the number of runs
		const numberOfRuns = this.runs.length;

		this.runs.forEach( ( run, index ) => {
			// Create a summary of each run
			const runSummary = [];

			// Add the run status
			runSummary.push( {
				label: escapeHTML( __( 'Status', 'uncanny-automator' ) ),
				value: html`
					<uap-log-status status-id="${ run.status_id }"></uap-log-status>
				`
			} );

			// Add the date
			runSummary.push( {
				label: escapeHTML( __( 'Date', 'uncanny-automator' ) ),
				value: run.date
			} );

			// Check if there is an error message
			if ( ! isEmpty( run.result_message ) ) {
				runSummary.push( {
					label: escapeHTML( __( 'Notes', 'uncanny-automator' ) ),
					value: decode( run.result_message )
				} );
			}

			runs.push( {
				title: html`
					<uap-log-status status-id="${ run.status_id }" hide-label></uap-log-status>
					${ sprintf(
						/* translators: 1. Number */
						escapeHTML( __( 'Try %1$s', 'uncanny-automator' ) ),
						`${ ( index + 1 ) }/${ numberOfRuns }`
					) }
				`,
				summary: runSummary,
				properties: [ ...this.fieldsForSidebar, ...run.properties ]
			} );
		} );

		return runs;
	}

	/**
	 * The message shown when the action data is missing
	 *
	 * @return {String} The message
	 */
	get dataMissingErrorMessage() {
		return escapeHTML( __( 'Note: The information about this action is unavailable because it was removed from the recipe.', 'uncanny-automator' ) );
	}
}
